import React from "react";

import {
  offerUserStatusOptions,
  pricingPlanColumn,
  userNameColumn,
} from "./offerUserColumns";
import { getOfferDescription } from "../../../utils/data/Offers";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import { dateColumn, statusColumn } from "../../dataTable/dataTableColumns";

import type { TFunction } from "i18next";

import type {
  OfferFreecopyUser,
  OfferFreecopyUserRow,
} from "./offerFreecopyColumns.types";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";

interface OfferFreecopyColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  isSvod: boolean;
}

export function offerFreecopyRow(
  user: OfferFreecopyUser,
  t: TFunction,
): OfferFreecopyUserRow {
  return {
    status: user.status,
    user_uuid: user.user_uuid,
    user_name: `${user.user.first_name} ${user.user.last_name}`,
    pricing_plan: getOfferDescription(t, user),
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- ?? would keep an empty date
    subscription_date: user.subscription_date || user.freecopy_start || null,
    next_billing_date: user.next_billing_date ?? null,
    rental_duration: user.rental_duration ?? null,
  };
}

export function offerFreecopyColumns({
  t,
  projectSlug,
  isSvod,
}: OfferFreecopyColumnsOptions): DataTableColumnDef<OfferFreecopyUserRow>[] {
  const durationTitle = t("editor-offers:rental_duration");

  return [
    statusColumn<OfferFreecopyUserRow>(
      "status",
      t("editor-offers:status"),
      offerUserStatusOptions(t),
    ),
    userNameColumn<OfferFreecopyUserRow>(t, projectSlug),
    pricingPlanColumn<OfferFreecopyUserRow>(t, {
      width: "27%",
      minWidth: 215,
      searchable: false,
    }),
    dateColumn<OfferFreecopyUserRow>(
      "subscription_date",
      t("editor-freecopy:subscriptionBegin"),
      { minWidth: 150 },
    ),
    isSvod
      ? dateColumn<OfferFreecopyUserRow>(
          "next_billing_date",
          t("editor-offers:next_billing"),
          { width: "18%", minWidth: 220 },
        )
      : {
          accessorKey: "rental_duration",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title={durationTitle} />
          ),
          meta: { label: durationTitle, width: "20%", minWidth: 115 },
          cell: ({ row }) => {
            const days = row.original.rental_duration;
            return days
              ? `${days} ${t("editor-analytics:frequency_type.day", { count: days })}`
              : "";
          },
        },
  ];
}
